import type { Monster } from './types'
import { applyGear } from './gear'
import { applyLearnedMoves } from './attacks'
import { generateMonster } from './generator'
import { applyLevel, pointsForWin, POINTS_LOSS } from './progression'

const KEY_MONSTERS = 'cryptoket.monsters'
const KEY_RECORD = 'cryptoket.record'

export interface BattleRecord {
  wins: number
  losses: number
  points: number
  streak: number
  bestStreak: number
}

const EMPTY_RECORD: BattleRecord = { wins: 0, losses: 0, points: 0, streak: 0, bestStreak: 0 }

export function loadMonsters(): Monster[] {
  const raw = localStorage.getItem(KEY_MONSTERS)
  if (raw) {
    try {
      return (JSON.parse(raw) as Monster[]).map((m) => applyGear(applyLevel(applyLearnedMoves(m))))
    } catch {
      // données corrompues → repart sur les starters
    }
  }
  const starters = [generateMonster('base'), generateMonster('meme')]
  saveMonsters(starters)
  return starters.map((m) => applyGear(applyLevel(applyLearnedMoves(m))))
}

export function saveMonsters(monsters: Monster[]): void {
  localStorage.setItem(KEY_MONSTERS, JSON.stringify(monsters))
}

export function addMonster(monster: Monster): Monster[] {
  const all = loadMonsters()
  all.unshift(monster)
  saveMonsters(all)
  return all
}

export function getMonster(id: string): Monster | undefined {
  return loadMonsters().find((m) => m.id === id)
}

export function loadRecord(): BattleRecord {
  const raw = localStorage.getItem(KEY_RECORD)
  if (raw) {
    try {
      // spread sur les défauts: migre les anciens records sans points/streak
      return { ...EMPTY_RECORD, ...(JSON.parse(raw) as Partial<BattleRecord>) }
    } catch {
      // ignore
    }
  }
  return { ...EMPTY_RECORD }
}

/** Ajoute des points de rang hors combat (récompenses de quêtes). */
export function grantPoints(delta: number): BattleRecord {
  const rec = loadRecord()
  rec.points = Math.max(0, rec.points + delta)
  localStorage.setItem(KEY_RECORD, JSON.stringify(rec))
  return rec
}

export function recordResult(won: boolean): { record: BattleRecord; delta: number } {
  const rec = loadRecord()
  let delta: number
  if (won) {
    delta = pointsForWin(rec.streak)
    rec.wins += 1
    rec.streak += 1
    rec.bestStreak = Math.max(rec.bestStreak, rec.streak)
  } else {
    delta = POINTS_LOSS
    rec.losses += 1
    rec.streak = 0
  }
  rec.points = Math.max(0, rec.points + delta)
  localStorage.setItem(KEY_RECORD, JSON.stringify(rec))
  return { record: rec, delta }
}
